import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import LockIcon from '@mui/icons-material/Lock';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import { usePermissions } from '../../context/AuthContext';
import SettingsErrorAlert from './SettingsErrorAlert';

const DomainSettings = () => {
  const { can } = usePermissions();
  const canEdit = can('settings', 'edit');
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState(null);
  const [domains, setDomains] = useState([]);
  const [newDomain, setNewDomain] = useState('');

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/settings/domains');
      setDomains(response.data.data?.domains || []);
    } catch (err) {
      setError({
        message: err.response?.data?.message || err.message || 'Failed to load domain settings',
        path: err.response?.data?.path,
        status: err.response?.status
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleAdd = async () => {
    const domain = newDomain.trim().toLowerCase();
    if (!domain) {
      toast.error('Enter a domain name');
      return;
    }
    setAdding(true);
    try {
      const response = await api.post('/settings/domains', { domain });
      setDomains(response.data.data?.domains || []);
      setNewDomain('');
      toast.success('Domain added');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add domain');
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (domainName) => {
    try {
      const response = await api.delete(`/settings/domains/${encodeURIComponent(domainName)}`);
      setDomains(response.data.data?.domains || []);
      toast.success('Domain removed');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to remove domain');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Domain Setup</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Configure allowed email domains for user accounts. <strong>pilvisystems.com</strong> is the default and cannot be removed.
          </Typography>
        </Box>
        <Button startIcon={<RefreshIcon />} onClick={load}>Refresh</Button>
      </Box>

      {error && <SettingsErrorAlert error={error} />}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>Allowed Domains</Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Domain</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {domains.map((domain) => (
                <TableRow key={domain.name}>
                  <TableCell>{domain.name}</TableCell>
                  <TableCell>
                    {domain.is_default && (
                      <Chip size="small" label="Default" color="primary" sx={{ mr: 1 }} />
                    )}
                    {domain.is_locked && (
                      <Chip size="small" icon={<LockIcon />} label="Locked" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {canEdit && !domain.is_locked && (
                      <Tooltip title="Remove domain">
                        <IconButton size="small" color="error" onClick={() => handleRemove(domain.name)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {canEdit && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>Add Domain</Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            New users can be created with an email address on any allowed domain.
          </Alert>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            <TextField
              label="Domain name"
              placeholder="example.com"
              value={newDomain}
              onChange={(e) => setNewDomain(e.target.value)}
              fullWidth
              helperText="Do not include @ — e.g. example.com"
            />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAdd}
              disabled={adding || !newDomain.trim()}
              sx={{ mt: 1, minWidth: 140 }}
            >
              Add Domain
            </Button>
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default DomainSettings;
